import React, { useContext } from 'react';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, StaticContext, SurveyTheme } from '@sm/webassets';
import { t, defineMessages } from '@sm/intl';
import Layout from '~app/components/Survey/Layout';
import Header from '~app/components/Survey/Header';
import QuizScoreSummary from '~app/components/Survey/QuizScoreSummary';
import SurveyTitle from '~app/components/Survey/SurveyTitle';
import SurveyPages from '~app/components/Survey/SurveyPages';
import ActionGroup from '~app/components/Survey/ActionGroup';
import GoToURL from '~app/components/Survey/GoToURL';
import Footer from '~app/components/Survey/Footer';
import getSpageGoogleFontsLink from '~app/helpers/spageGoogleFonts';
import getSpageWindowTitle from '~app/helpers/spageWindowTitle';
import { getSurveyForCollector, getQuizSurveyWithResponsesQM } from './QuizResultQM.graphql';
import formatSurvey from './helper/transform';
import './quiz-result.scss';

const COPY = defineMessages({
  SM_META_DESCRIPTION: {
    id: 'QuizResult.MetaSmMetaDescription',
    defaultMessage: "Create your own online survey now with SurveyMonkey's expert certified FREE templates.",
    description: '[Type: label][Vis: med] - Trailing text for core product domain description meta tag',
  },
  META_DESCRIPTION: {
    id: 'QuizResult.MetaDescription',
    defaultMessage: 'Your feedback is appreciated!',
    description:
      "Web survey powered by SurveyMonkey.com. Create your own online survey now with SurveyMonkey's expert certified FREE templates.",
  },
  WEB_POWERED_BY: {
    id: 'QuizResult.MetaPoweredBy',
    defaultMessage: 'Web survey powered by {domainName}',
    description: '[Type: label][Vis: med] - Text for non core product domains in meta tag',
  },
});

const QuizResult = () => {
  const {
    sm,
    pageOptions: { languageCode: langCode, isKiosk, isCustomDomain },
  } = useContext(StaticContext);
  const collectorId = sm?.smParams?.cid;
  const langId = sm?.smParams?.langId;
  const respondentSurveyVersion = sm?.smParams?.rsv;
  const qrUrl = sm?.qaDoneUrl;
  const smSlug = sm?.smStr;

  const {
    loading: loadingCollector,
    error: errorCollector,
    data: collectorData,
  } = useQuery(getSurveyForCollector, {
    context: {
      targetPath: '/lo-graphql/collector',
      headers: {
        'X-Collector-Id': collectorId,
        'X-Language-Id': langId,
      },
    },
    variables: {
      collectorId,
    },
  });

  const qzResultsHeaders = {
    'X-SM-PARAM': smSlug,
  };

  const {
    loading: loadingRespondent,
    error: errorRespondent,
    data: respondentData,
  } = useQuery(getQuizSurveyWithResponsesQM, {
    skip: !collectorData || loadingCollector,
    variables: {
      surveyId: collectorData?.collector?.surveyId,
      smParams: smSlug,
      languageCode: langCode,
    },
    context: {
      targetPath: '/lo-graphql/quizResults',
      headers: qzResultsHeaders,
    },
  });

  const surveyData = respondentData?.survey;

  if (errorCollector || errorRespondent || (!loadingRespondent && respondentData?.respondent === null)) {
    // send corrupted/missing respondent to next end page
    return <GoToURL url={qrUrl} />;
  }

  if (surveyData) {
    if (respondentSurveyVersion !== 'None' && surveyData.version !== Number(respondentSurveyVersion)) {
      const redirectUrl = collectorData?.collector?.allowMultipleResponses
        ? `/r/?sm=${smSlug}`
        : `/survey-taken/?sm=${smSlug}`;
      return <GoToURL url={redirectUrl} />;
    }

    const surveyId = collectorData?.collector?.surveyId;
    const surveyLogo = surveyData?.design?.logo;
    const numberingEnabled = surveyData?.design?.questionNumbering?.enabled;
    const showQuizAnswers = surveyData?.quizOptions?.showAnswers;
    const scoreBase = surveyData?.quizOptions?.rangeType;
    const titleEnabled = surveyData?.design?.surveyTitle?.enabled;
    const footerEnabled =
      surveyData?.design?.footer?.enabled && !isCustomDomain && !isKiosk && collectorData?.collector?.type !== 'POPUP';

    const surveyTitle = surveyData?.titleHTML || surveyData?.title;
    const surveyPages = surveyData?.pages?.items;
    const isGrayScale = surveyData?.design?.footer?.grayscale;
    const spageGoogleFontsLink = getSpageGoogleFontsLink(surveyData?.design?.theme);
    const isRTL = ['ar', 'he', 'fa'].indexOf(langCode) > -1;
    const surveyResults = respondentData?.respondentObfuscated?.resultsQM;
    const quizScore = respondentData?.respondentObfuscated?.quizSummary;
    const surveyWithResults = formatSurvey(surveyResults, numberingEnabled, showQuizAnswers);

    return (
      <BasePage
        actionFlow="QuizResults"
        pageId="QuizResults"
        legacyWeb="respweb"
        includeHeader={false}
        includeFooter={false}
      >
        <Helmet htmlAttributes={{ lang: langCode }}>
          <title>{getSpageWindowTitle(titleEnabled, surveyData?.title)}</title>
          <meta name="description" content={t(COPY.META_DESCRIPTION)} />
          <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes" />
          <meta httpEquiv="content-language" content={langCode} />
          <link rel="dns-prefetch" href="//fonts.googleapis.com" />
          <link rel="dns-prefetch" href="//fonts.gstatic.com" />
          <link rel="preconnect" href="//fonts.googleapis.com" />
          <link rel="preconnect" href="//fonts.gstatic.com" />
          <If condition={spageGoogleFontsLink}>
            <link rel="stylesheet" href={spageGoogleFontsLink} async />
          </If>
        </Helmet>
        <SurveyTheme surveyId={surveyId} targetEndPoint="quizResults" headers={qzResultsHeaders}>
          <Layout isRTL={isRTL}>
            <Header logo={surveyLogo} />
            <QuizScoreSummary score={quizScore} surveyId={surveyId} base={scoreBase} />
            <If condition={titleEnabled}>
              <SurveyTitle title={surveyTitle} />
            </If>
            <SurveyPages pages={surveyPages} survey={surveyWithResults} />
            <ActionGroup qrDoneUrl={qrUrl} />
            <If condition={footerEnabled}>
              <Footer isGrayScale={isGrayScale} />
            </If>
          </Layout>
        </SurveyTheme>
      </BasePage>
    );
  }
  return null;
};

export default QuizResult;
